import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Heart, Users, Handshake, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { API_URL } from "../lib/utils";

export const GetInvolved = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("volunteer");

  // Independent loading states
  const [loading, setLoading] = useState({
    volunteer: false,
    partner: false,
    donation: false,
  });

  // Form states
  const [volunteerForm, setVolunteerForm] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    availability: "",
    motivation: "",
  });

  const [partnerForm, setPartnerForm] = useState({
    organization_name: "",
    contact_person: "",
    email: "",
    phone: "",
    partnership_type: "",
    message: "",
  });

  const [donationForm, setDonationForm] = useState({
    name: "",
    email: "",
    amount: "",
    currency: "NGN",
    purpose: "",
    is_anonymous: false,
  });

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      setActiveTab(hash);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  const setFormLoading = (formKey, value) => {
    setLoading((prev) => ({ ...prev, [formKey]: value }));
  };

  const parseApiError = async (response, fallback = "Request failed.") => {
    let data = {};
    try {
      data = await response.json();
    } catch {
      // ignore json parse errors
    }

    if (typeof data?.detail === "string" && data.detail.trim()) {
      return data.detail;
    }

    if (Array.isArray(data?.detail)) {
      // FastAPI validation format
      const msgs = data.detail
        .map((d) => {
          const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : "";
          const msg = d?.msg || "";
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (msgs.length) return msgs.join(" | ");
    }

    return fallback;
  };

  // -------------------------
  // Volunteer
  // -------------------------
  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      name: volunteerForm.name.trim(),
      email: volunteerForm.email.trim(),
      phone: volunteerForm.phone.trim(),
      skills: volunteerForm.skills.trim(),
      availability: volunteerForm.availability.trim(),
      motivation: volunteerForm.motivation.trim(),
    };

    if (
      !payload.name ||
      !payload.email ||
      !payload.phone ||
      !payload.skills ||
      !payload.availability ||
      !payload.motivation
    ) {
      toast.error("Please complete all required volunteer fields.");
      return;
    }

    setFormLoading("volunteer", true);
    try {
      const response = await fetch(`${API_URL}/volunteer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to submit volunteer form."));
      }

      toast.success("Thank you for your volunteer application! We will contact you soon.");
      setVolunteerForm({
        name: "",
        email: "",
        phone: "",
        skills: "",
        availability: "",
        motivation: "",
      });
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
      console.error("Volunteer submit error:", error);
    } finally {
      setFormLoading("volunteer", false);
    }
  };

  // -------------------------
  // Partner
  // -------------------------
  const handlePartnerSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      organization_name: partnerForm.organization_name.trim(),
      contact_person: partnerForm.contact_person.trim(),
      email: partnerForm.email.trim(),
      phone: partnerForm.phone.trim(),
      partnership_type: partnerForm.partnership_type.trim(),
      message: partnerForm.message.trim(),
    };

    if (
      !payload.organization_name ||
      !payload.contact_person ||
      !payload.email ||
      !payload.phone ||
      !payload.partnership_type ||
      !payload.message
    ) {
      toast.error("Please complete all required partner fields.");
      return;
    }

    setFormLoading("partner", true);
    try {
      const response = await fetch(`${API_URL}/partner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to submit partnership request."));
      }

      toast.success("Thank you for your partnership interest! We will be in touch.");
      setPartnerForm({
        organization_name: "",
        contact_person: "",
        email: "",
        phone: "",
        partnership_type: "",
        message: "",
      });
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
      console.error("Partner submit error:", error);
    } finally {
      setFormLoading("partner", false);
    }
  };

  // -------------------------
  // Donation
  // -------------------------
  const handleDonationSubmit = async (e) => {
    e.preventDefault();

    const amountNum = Number(donationForm.amount);

    const payload = {
      name: donationForm.name.trim(),
      email: donationForm.email.trim(),
      amount: Number.isFinite(amountNum) ? amountNum : NaN,
      currency: donationForm.currency,
      purpose: donationForm.purpose.trim(),
      is_anonymous: Boolean(donationForm.is_anonymous),
    };

    if (!payload.name || !payload.email || !payload.purpose) {
      toast.error("Please complete all required donation fields.");
      return;
    }

    if (!Number.isFinite(payload.amount) || payload.amount < 100) {
      toast.error("Donation amount must be a valid number (minimum 100).");
      return;
    }

    setFormLoading("donation", true);
    try {
      const response = await fetch(`${API_URL}/donation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response, "Failed to process donation."));
      }

      // Keep summary for Donate Now page if needed
      sessionStorage.setItem(
        "GLGIF_donation_summary",
        JSON.stringify({
          name: payload.name,
          email: payload.email,
          amount: payload.amount,
          currency: payload.currency,
          purpose: payload.purpose,
          is_anonymous: payload.is_anonymous,
          created_at: new Date().toISOString(),
        })
      );

      toast.success("Donation form submitted successfully. Redirecting to Donate Now...");

      setDonationForm({
        name: "",
        email: "",
        amount: "",
        currency: "NGN",
        purpose: "",
        is_anonymous: false,
      });

      // Redirect to Donate Now page (your required flow)
      window.location.href = "/donate-now";
    } catch (error) {
      toast.error(error.message || "An error occurred. Please try again.");
      console.error("Donation submit error:", error);
    } finally {
      setFormLoading("donation", false);
    }
  };

  const tabs = [
    { id: "volunteer", label: "Volunteer", icon: Users },
    { id: "partner", label: "Partner", icon: Handshake },
    { id: "donate", label: "Donate", icon: Heart },
  ];

  return (
    <div data-testid="get-involved-page">
      {/* Hero */}
      <section className="relative py-20 md:py-32 text-white overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/hero-partner.png')" }}
        />

        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-stone-900/65" />

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="font-accent text-xl text-amber-400 mb-4">Join Us</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">Get Involved</h1>
            <p className="text-lg text-stone-200">
              Lasting impact happens when people come together. GLGIF welcomes individuals,
              NGOs, businesses, schools, faith-based institutions, and community leaders.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Support */}
      <section className="py-12 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-4 justify-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-amber-700 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid={`tab-${tab.id}`}
                type="button"
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Volunteer Form */}
          {activeTab === "volunteer" && (
            <div id="volunteer" data-testid="volunteer-section">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-amber-700" />
                    </div>
                    Become a Volunteer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleVolunteerSubmit} className="space-y-6" data-testid="volunteer-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="vol-name">Full Name *</Label>
                        <Input
                          id="vol-name"
                          value={volunteerForm.name}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                          required
                          data-testid="volunteer-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vol-email">Email Address *</Label>
                        <Input
                          id="vol-email"
                          type="email"
                          value={volunteerForm.email}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                          required
                          data-testid="volunteer-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="vol-phone">Phone Number *</Label>
                        <Input
                          id="vol-phone"
                          type="tel"
                          value={volunteerForm.phone}
                          onChange={(e) => setVolunteerForm({ ...volunteerForm, phone: e.target.value })}
                          required
                          data-testid="volunteer-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vol-availability">Availability *</Label>
                        <Select
                          value={volunteerForm.availability}
                          onValueChange={(value) =>
                            setVolunteerForm({ ...volunteerForm, availability: value })
                          }
                        >
                          <SelectTrigger data-testid="volunteer-availability">
                            <SelectValue placeholder="Select availability" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekends">Weekends Only</SelectItem>
                            <SelectItem value="weekdays">Weekdays Only</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                            <SelectItem value="events-only">Events Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vol-skills">Skills & Experience *</Label>
                      <Input
                        id="vol-skills"
                        value={volunteerForm.skills}
                        onChange={(e) => setVolunteerForm({ ...volunteerForm, skills: e.target.value })}
                        placeholder="e.g., Teaching, Healthcare, Technology, Community Outreach"
                        required
                        data-testid="volunteer-skills"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vol-motivation">Why do you want to volunteer with GLGIF? *</Label>
                      <Textarea
                        id="vol-motivation"
                        value={volunteerForm.motivation}
                        onChange={(e) =>
                          setVolunteerForm({ ...volunteerForm, motivation: e.target.value })
                        }
                        rows={4}
                        required
                        data-testid="volunteer-motivation"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading.volunteer}
                      className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-full py-6"
                      data-testid="volunteer-submit"
                    >
                      {loading.volunteer ? "Submitting..." : "Submit Application"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Partner Form */}
          {activeTab === "partner" && (
            <div id="partner" data-testid="partner-section">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Handshake className="w-6 h-6 text-blue-700" />
                    </div>
                    Become a Partner
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePartnerSubmit} className="space-y-6" data-testid="partner-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="partner-org">Organization Name *</Label>
                        <Input
                          id="partner-org"
                          value={partnerForm.organization_name}
                          onChange={(e) =>
                            setPartnerForm({ ...partnerForm, organization_name: e.target.value })
                          }
                          required
                          data-testid="partner-org"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="partner-contact">Contact Person *</Label>
                        <Input
                          id="partner-contact"
                          value={partnerForm.contact_person}
                          onChange={(e) =>
                            setPartnerForm({ ...partnerForm, contact_person: e.target.value })
                          }
                          required
                          data-testid="partner-contact"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="partner-email">Email Address *</Label>
                        <Input
                          id="partner-email"
                          type="email"
                          value={partnerForm.email}
                          onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                          required
                          data-testid="partner-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="partner-phone">Phone Number *</Label>
                        <Input
                          id="partner-phone"
                          type="tel"
                          value={partnerForm.phone}
                          onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })}
                          required
                          data-testid="partner-phone"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="partner-type">Partnership Type *</Label>
                      <Select
                        value={partnerForm.partnership_type}
                        onValueChange={(value) =>
                          setPartnerForm({ ...partnerForm, partnership_type: value })
                        }
                      >
                        <SelectTrigger data-testid="partner-type">
                          <SelectValue placeholder="Select partnership type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="corporate">Corporate Partnership</SelectItem>
                          <SelectItem value="ngo">NGO Collaboration</SelectItem>
                          <SelectItem value="educational">Educational Institution</SelectItem>
                          <SelectItem value="faith-based">Faith-Based Organization</SelectItem>
                          <SelectItem value="media">Media Partnership</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {!partnerForm.partnership_type && (
                        <p className="text-xs text-stone-500">Please choose a partnership type.</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="partner-message">Partnership Proposal *</Label>
                      <Textarea
                        id="partner-message"
                        value={partnerForm.message}
                        onChange={(e) => setPartnerForm({ ...partnerForm, message: e.target.value })}
                        rows={4}
                        placeholder="Tell us how you'd like to partner with GLGIF"
                        required
                        data-testid="partner-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading.partner}
                      className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-full py-6"
                      data-testid="partner-submit"
                    >
                      {loading.partner ? "Submitting..." : "Submit Partnership Request"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Donate Form */}
          {activeTab === "donate" && (
            <div id="donate" data-testid="donate-section">
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <Heart className="w-6 h-6 text-green-700" />
                    </div>
                    Make a Donation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleDonationSubmit} className="space-y-6" data-testid="donation-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="donate-name">Full Name *</Label>
                        <Input
                          id="donate-name"
                          value={donationForm.name}
                          onChange={(e) => setDonationForm({ ...donationForm, name: e.target.value })}
                          required
                          data-testid="donation-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="donate-email">Email Address *</Label>
                        <Input
                          id="donate-email"
                          type="email"
                          value={donationForm.email}
                          onChange={(e) => setDonationForm({ ...donationForm, email: e.target.value })}
                          required
                          data-testid="donation-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="donate-amount">Amount *</Label>
                        <Input
                          id="donate-amount"
                          type="number"
                          min="100"
                          value={donationForm.amount}
                          onChange={(e) => setDonationForm({ ...donationForm, amount: e.target.value })}
                          placeholder="Enter amount"
                          required
                          data-testid="donation-amount"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="donate-currency">Currency</Label>
                        <Select
                          value={donationForm.currency}
                          onValueChange={(value) => setDonationForm({ ...donationForm, currency: value })}
                        >
                          <SelectTrigger data-testid="donation-currency">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NGN">NGN (₦)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="GBP">GBP (£)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="donate-purpose">Donation Purpose *</Label>
                      <Select
                        value={donationForm.purpose}
                        onValueChange={(value) => setDonationForm({ ...donationForm, purpose: value })}
                      >
                        <SelectTrigger data-testid="donation-purpose">
                          <SelectValue placeholder="Select purpose" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Support</SelectItem>
                          <SelectItem value="tatl1000">Touch A Thousand Lives (TATL 1000)</SelectItem>
                          <SelectItem value="education">Send a Child to School</SelectItem>
                          <SelectItem value="feeding">Feed the Child Campaign</SelectItem>
                          <SelectItem value="tech">Tech a School</SelectItem>
                          <SelectItem value="relief">Humanitarian Relief</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="anonymous"
                        checked={donationForm.is_anonymous}
                        onChange={(e) =>
                          setDonationForm({ ...donationForm, is_anonymous: e.target.checked })
                        }
                        className="w-5 h-5"
                        data-testid="donation-anonymous"
                      />
                      <Label htmlFor="anonymous">Make this donation anonymous</Label>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading.donation}
                      className="w-full bg-green-700 hover:bg-green-800 text-white rounded-full py-6"
                      data-testid="donation-submit"
                    >
                      {loading.donation ? "Processing..." : "Complete Donation"}
                    </Button>

                    <p className="text-sm text-stone-500 text-center">
                      Note: This is a donation pledge form. You will be redirected to Donate Now after submission.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Other Ways to Help */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-stone-900 mb-4">Other Ways to Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Sponsor a Project", desc: "Fund specific initiatives" },
              { title: "Donate Materials", desc: "Provide relief supplies" },
              { title: "Share Our Story", desc: "Amplify on social media" },
              { title: "Pray With Us", desc: "Join our prayer network" },
            ].map((item, index) => (
              <Card key={item.title} className="card-hover text-center" data-testid={`support-option-${index}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6 text-amber-700" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
